// Cliente de la API de RINDE. Maneja tokens, renovación de sesión y
// la cola offline para los técnicos que capturan gastos sin señal.

// Vacio = mismo origen. El backend sirve la app y la API juntas, asi
// que no hace falta dominio absoluto ni CORS.
// Se exporta para que las pantallas puedan armar la dirección de una
// foto adjunta, que se sirve como archivo y no como JSON.
export const BASE = process.env.REACT_APP_API_URL || '';

const LS_ACCESS  = 'rinde.access';
const LS_REFRESH = 'rinde.refresh';
const LS_USUARIO = 'rinde.usuario';
const LS_COLA    = 'rinde.cola';

export const tokens = {
  get access()  { return localStorage.getItem(LS_ACCESS); },
  get refresh() { return localStorage.getItem(LS_REFRESH); },
  get usuario() {
    try { return JSON.parse(localStorage.getItem(LS_USUARIO) || 'null'); }
    catch { return null; }
  },
  guardar({ accessToken, refreshToken, usuario }) {
    if (accessToken)  localStorage.setItem(LS_ACCESS, accessToken);
    if (refreshToken) localStorage.setItem(LS_REFRESH, refreshToken);
    if (usuario)      localStorage.setItem(LS_USUARIO, JSON.stringify(usuario));
  },
  limpiar() {
    [LS_ACCESS, LS_REFRESH, LS_USUARIO].forEach((k) => localStorage.removeItem(k));
  },
};

export const colaOffline = {
  leer() {
    try { return JSON.parse(localStorage.getItem(LS_COLA) || '[]'); }
    catch { return []; }
  },
  agregar(item) {
    const cola = colaOffline.leer();
    cola.push({ ...item, encoladoEn: new Date().toISOString() });
    localStorage.setItem(LS_COLA, JSON.stringify(cola));
    return cola.length;
  },
  reemplazar(cola) { localStorage.setItem(LS_COLA, JSON.stringify(cola)); },
};

// Una sola renovación concurrente, aunque fallen varias peticiones a la vez.
let refrescando = null;

async function refrescarToken() {
  if (refrescando) return refrescando;
  refrescando = (async () => {
    const u = tokens.usuario;
    if (!u || !tokens.refresh) throw new Error('sin_sesion');
    const res = await fetch(`${BASE}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: tokens.refresh, usuarioId: u.id }),
    });
    if (!res.ok) { tokens.limpiar(); throw new Error('sesion_expirada'); }
    const { accessToken } = await res.json();
    tokens.guardar({ accessToken });
    return accessToken;
  })().finally(() => { refrescando = null; });
  return refrescando;
}

async function pedir(ruta, opciones = {}, reintentando = false) {
  // Sin limite de tiempo, una peticion que no responde deja la
  // pantalla en "Cargando…" para siempre. Mejor fallar y decirlo.
  const corte = AbortSignal.timeout ? AbortSignal.timeout(25_000) : undefined;

  let res;
  try {
    res = await fetch(`${BASE}${ruta}`, {
      signal: corte,
      ...opciones,
      headers: {
        'Content-Type': 'application/json',
        ...(tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {}),
        ...(opciones.headers || {}),
      },
    });
  } catch (err) {
    const e = new Error(
      err.name === 'TimeoutError' || err.name === 'AbortError'
        ? 'sin_respuesta'
        : 'sin_conexion',
    );
    e.sinRespuesta = true;
    throw e;
  }

  // ── Un 401 del cambio de contraseña NO es un token vencido ───────
  //
  // Cuando alguien escribe mal su contraseña actual, el servidor
  // responde 401 `password_actual_incorrecta`. El bloque de abajo lo
  // tomaba por un token vencido, intentaba refrescar, fallaba —porque
  // el token está perfecto— y terminaba echando a la persona de la
  // sesión con "sesión expirada".
  //
  // O sea: te equivocabas al teclear tu clave vieja y la plataforma te
  // sacaba, sin decirte nunca que el problema era esa clave.
  const esCambioDePassword = ruta.includes('/change-password');

  if (res.status === 401 && !reintentando && tokens.refresh && !esCambioDePassword) {
    try {
      await refrescarToken();
      return pedir(ruta, opciones, true);
    } catch {
      window.dispatchEvent(new CustomEvent('rinde:sesion-expirada'));
      throw new Error('sesion_expirada');
    }
  }

  if (res.status === 428) {
    window.dispatchEvent(new CustomEvent('rinde:cambio-password'));
    throw new Error('cambio_password_requerido');
  }

  if (!res.ok) {
    let detalle = {};
    try { detalle = await res.json(); } catch { /* sin cuerpo */ }
    const err = new Error(detalle.error || `http_${res.status}`);
    err.status = res.status;
    err.detalle = detalle;
    throw err;
  }

  return res.status === 204 ? null : res.json();
}

const get   = (r)    => pedir(r);
const post  = (r, b) => pedir(r, { method: 'POST',  body: JSON.stringify(b ?? {}) });
const patch = (r, b) => pedir(r, { method: 'PATCH', body: JSON.stringify(b ?? {}) });

export const api = {
  async login(username, password) {
    const r = await post('/api/auth/login', { username, password });
    tokens.guardar(r);
    return r;
  },
  async logout() {
    try { await post('/api/auth/logout'); } finally { tokens.limpiar(); }
  },
  cambiarPassword: (passwordActual, passwordNueva) =>
    post('/api/auth/change-password', { passwordActual, passwordNueva }),
  yo: () => get('/api/auth/me'),

  gastos:       (f = {}) => get(`/api/gastos?${new URLSearchParams(f)}`),
  gasto:        (id)     => get(`/api/gastos/${id}`),
  crearGasto:   (g)      => post('/api/gastos', g),
  decidirGasto: (id, decision, observacion) =>
                            post(`/api/gastos/${id}/decision`, { decision, observacion }),
  kpis:         ()       => get('/api/gastos/resumen/kpis'),

  catalogos:    ()       => get('/api/gastos/catalogos/opciones'),
  editarGasto: (id, cambios) => patch(`/api/gastos/${id}`, cambios),
  revisionGasto: (id) => get(`/api/gastos/${id}/revision`),
  contabilizarGasto: (id, sinSustento) =>
    post(`/api/gastos/${id}/contabilizar`, { sinSustento: Boolean(sinSustento) }),
  gastoEliminable: (id) => get(`/api/gastos/${id}/eliminable`),
  borrarGasto: (id, motivo) => pedir(`/api/gastos/${id}`,
    { method: 'DELETE', body: JSON.stringify({ motivo }) }),

  viaticoEliminable: (id) => get(`/api/viaticos/${id}/eliminable`),
  borrarViatico: (id, motivo) => pedir(`/api/viaticos/${id}`,
    { method: 'DELETE', body: JSON.stringify({ motivo }) }),

  editarEncargo: (id, c) => patch(`/api/encargos/${id}`, c),
  encargoEliminable: (id) => get(`/api/encargos/${id}/eliminable`),
  borrarEncargo: (id, motivo) => pedir(`/api/encargos/${id}`,
    { method: 'DELETE', body: JSON.stringify({ motivo }) }),

  // Firma propia
  miFirma:      ()            => get('/api/auth/firma'),
  guardarFirma: (trazo, dni)  => pedir('/api/auth/firma',
                                   { method: 'PUT', body: JSON.stringify({ trazo, dni }) }),
  borrarFirma:  ()            => pedir('/api/auth/firma', { method: 'DELETE' }),
  leerFoto: (imagen, tipoMime) => post('/api/gastos/leer-foto', { imagen, tipoMime }),

  // Verificación de proveedores
  estadoSunat:        ()    => get('/api/proveedores/sunat/estado'),
  erroresRecientes:   ()    => get('/api/diagnostico/errores'),
  firmasEquipo:       ()    => get('/api/equipo/firmas'),
  // Todos los activos: a quién se le puede depositar, que no es lo
  // mismo que a quién se supervisa.
  depositables:       ()    => get('/api/depositables'),
  consultarRuc:       (ruc) => get(`/api/proveedores/${ruc}`),
  alertasProveedores: ()    => get('/api/proveedores/alertas/gastos'),

  // Padron de personal
  personal:        (f = {}) => get(`/api/personal?${new URLSearchParams(f)}`),
  crearPersonal:   (d)      => post('/api/personal', d),
  editarPersonal:  (id, d)  => patch(`/api/personal/${id}`, d),
  usoPersonal:     (id)     => get(`/api/personal/${id}/uso`),
  bajaPersonal:    (id, motivo) => post(`/api/personal/${id}/baja`, { motivo }),
  reincorporarPersonal: (id) => post(`/api/personal/${id}/baja`, { reincorporar: true }),
  borrarPersonal:  (id)     => pedir(`/api/personal/${id}`, { method: 'DELETE' }),
  importarPersonal:(texto)  => post('/api/personal/importar', { texto }),

  // Documentos internos: impresion y firma
  documentoGasto:   (id) => get(`/api/documentos/gasto/${id}`),
  documentoViatico: (id) => get(`/api/documentos/viatico/${id}`),
  documentoEncargo: (id) => get(`/api/documentos/encargo/${id}`),
  firmarDocumento:  (d)  => post('/api/documentos/firmar', d),
  anularFirma:      (id) => pedir(`/api/documentos/firma/${id}`, { method: 'DELETE' }),
  marcarImpreso: (entidad, id) => post(`/api/documentos/impreso/${entidad}/${id}`),

  // Impresion por lote. Por defecto el servidor devuelve solo los que
  // no se han impreso; con todos=1 devuelve tambien los ya impresos.
  documentosPendientes: (soloSinImprimir) =>
    get(`/api/documentos/pendientes${soloSinImprimir ? '' : '?todos=1'}`),
  documentosLote: (ids) => post('/api/documentos/lote', { ids }),
  marcarImpresos: (ids) => post('/api/documentos/lote/impreso', { ids }),

  // Reportes
  reporteResumen:   (f = {}) => get(`/api/reportes/resumen?${new URLSearchParams(f)}`),
  reportePareto:    (f = {}) => get(`/api/reportes/pareto?${new URLSearchParams(f)}`),
  reportePorPersona:(f = {}) => get(`/api/reportes/por-persona?${new URLSearchParams(f)}`),
  reportePorOv:     (f = {}) => get(`/api/reportes/por-ov?${new URLSearchParams(f)}`),
  // Desglose de un proyecto: en que se fue y cada gasto. 'sin-ov' es el
  // grupo de los gastos generales, que no tienen OV.
  reporteProyecto:  (ovId, f = {}) =>
    get(`/api/reportes/proyecto/${encodeURIComponent(ovId)}?${new URLSearchParams(f)}`),
  // ── SAP por DTW ──
  dtwPrevio:    (f = {}) => get(`/api/dtw/previo?${new URLSearchParams(f)}`),
  dtwGenerar:   (f = {}) => post('/api/dtw/generar', f),
  dtwReembolsos:(f = {}) => get(`/api/dtw/reembolsos?${new URLSearchParams(f)}`),
  reporteTendencia: (f = {}) => get(`/api/reportes/tendencia?${new URLSearchParams(f)}`),
  reporteDesviaciones: (f = {}) => get(`/api/reportes/desviaciones?${new URLSearchParams(f)}`),
  causaDesviacion: (cuenta, f = {}) =>
    get(`/api/reportes/desviaciones/${cuenta}?${new URLSearchParams(f)}`),
  reportePresupuesto: (f = {}) => get(`/api/reportes/presupuesto?${new URLSearchParams(f)}`),
  guardarPresupuesto: (ovId, d) => pedir(`/api/reportes/presupuesto/${ovId}`,
    { method: 'PUT', body: JSON.stringify(d) }),

  // El CSV se descarga con el token puesto, por eso no es un enlace directo.
  async exportarGastos(f = {}) {
    const res = await fetch(`${BASE}/api/reportes/exportar?${new URLSearchParams(f)}`, {
      headers: tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {},
    });
    if (!res.ok) throw new Error('no_se_pudo_exportar');
    return res.blob();
  },
  /**
   * Trae un adjunto CON la sesión.
   *
   * Una etiqueta <img src="..."> no envía el token, así que el servidor
   * devolvía 401 y la foto salía rota. Aquí se pide con la sesión y se
   * convierte en una dirección temporal del navegador que <img> sí puede
   * mostrar.
   */
  async adjunto(gastoId, adjuntoId) {
    const res = await fetch(`${BASE}/api/gastos/${gastoId}/adjuntos/${adjuntoId}`, {
      headers: tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {},
    });
    if (!res.ok) throw new Error('no_se_pudo_abrir');
    return URL.createObjectURL(await res.blob());
  },

  leerQr:       (texto)  => post('/api/gastos/qr', { texto }),
  tipoCambio:   (fecha, moneda = 'USD') =>
    get(`/api/gastos/tipo-cambio?fecha=${encodeURIComponent(fecha)}`
      + `&moneda=${encodeURIComponent(moneda)}`),
  subirAdjunto: (id, a)  => post(`/api/gastos/${id}/adjuntos`, a),
  urlAdjunto:   (id, aid) => `${BASE}/api/gastos/${id}/adjuntos/${aid}`,

  encargos:           (abiertos) => get(`/api/encargos${abiertos ? '?abiertos=1' : ''}`),
  movimientosEncargo: (id)       => get(`/api/encargos/${id}/movimientos`),
  asignarEncargo:     (d)        => post('/api/encargos', d),

  ovs:      (abiertas) => get(`/api/ovs${abiertas ? '?abiertas=1' : ''}`),
  crearOv:  (ov)       => post('/api/ovs', ov),
  editarOv: (id, c)    => patch(`/api/ovs/${id}`, c),
  usoOv:    (id)       => get(`/api/ovs/${id}/uso`),
  borrarOv: (id, motivo) =>
    pedir(`/api/ovs/${id}`, { method: 'DELETE', body: JSON.stringify({ motivo }) }),

  // Viaticos
  viaticos:      (f = {}) => get(`/api/viaticos?${new URLSearchParams(f)}`),
  viatico:       (id)     => get(`/api/viaticos/${id}`),
  crearViatico:  (d)      => post('/api/viaticos', d),
  editarViatico: (id, d)  => patch(`/api/viaticos/${id}`, d),
  decidirViatico: (id, decision, observacion) =>
                             post(`/api/viaticos/${id}/decision`, { decision, observacion }),
  liquidacionViatico: (id) => get(`/api/viaticos/${id}/liquidacion`),


  equipo:        (todos) => get(`/api/equipo${todos ? '?todos=1' : ''}`),
  crearUsuario:  (u)     => post('/api/usuarios', u),
  editarUsuario: (id, c) => patch(`/api/usuarios/${id}`, c),
  resetPassword: (id)    => post(`/api/usuarios/${id}/reset-password`),
  // Por qué alguien no puede entrar. Solo dirección, y de a una cuenta.
  accesoUsuario: (id)    => get(`/api/usuarios/${id}/acceso`),

  estadoSap:      ()   => get('/api/sap/estado'),
  drenarSap:      ()   => post('/api/sap/drenar'),
  encolarSap:     (id) => post(`/api/sap/encolar/${id}`),
  reintentarSap:  (j)  => post(`/api/sap/reintentar/${j}`),
  reencolarHuerfanos: () => post('/api/sap/reencolar-huerfanos'),

  salud: () => get('/api/health'),
};

/** Sube los gastos capturados sin señal. */
export async function sincronizarCola() {
  const cola = colaOffline.leer();
  if (cola.length === 0) return { sincronizados: 0, pendientes: 0 };

  const quedan = [];
  let sincronizados = 0;

  for (const item of cola) {
    try {
      await api.crearGasto(item.payload);
      sincronizados++;
    } catch (err) {
      // 400 = dato inválido, no mejora reintentando: se descarta para no
      // bloquear la cola. Cualquier otro error se reintenta después.
      if (err.status !== 400) quedan.push(item);
    }
  }

  colaOffline.reemplazar(quedan);
  return { sincronizados, pendientes: quedan.length };
}
